import React, { useState, useEffect } from 'react';

// Milestone structure: { id, name, type, month, day }

/* MilestoneManagerProps:
   milestones, setMilestones, onUnsavedDataChange
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const MilestoneManager = ({
  milestones,
  setMilestones,
  onUnsavedDataChange,
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('birthday');
  const [date, setDate] = useState(''); // Stores as YYYY-MM-DD from date picker

  // Notify parent when there's unsaved data
  useEffect(() => {
    const hasUnsavedData = name.trim() !== '' || date !== '';
    onUnsavedDataChange?.(hasUnsavedData);
  }, [name, date, onUnsavedDataChange]);

  const handleAdd = () => {
    if (!name.trim() || !date) {
      alert('Please fill in all fields');
      return;
    }

    // Parse date (YYYY-MM-DD format)
    const dateObj = new Date(date + 'T00:00:00');
    const monthName = MONTHS[dateObj.getMonth()];
    const dayNum = dateObj.getDate();

    const newMilestone = {
      id: `${Date.now()}-${Math.random()}`,
      name: name.trim(),
      type,
      month: monthName,
      day: dayNum,
    };

    setMilestones([...milestones, newMilestone]);

    // Reset form
    setName('');
    setDate('');
  };

  const handleRemove = (id) => {
    setMilestones(milestones.filter(m => m.id !== id));
  };

  const formatDate = (month, day) => {
    return `${month} ${day}`;
  };

  const getTypeLabel = (type) => {
    return type === 'birthday' ? 'Birthday' : 'Work Anniversary';
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Birthdays & Work Anniversaries (Optional)
      </label>
      <p className="text-sm text-gray-500 mb-3">
        Add team member birthdays and work anniversaries. The AI will create celebration posts on the appropriate dates.
      </p>

      {/* Input Form */}
      <div className="post-planner-milestone-panel">
        <div className="post-planner-milestone-grid">
          <div className="post-planner-milestone-input">
            <label htmlFor="milestone-name">
              Person's Name
            </label>
            <input
              type="text"
              id="milestone-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Dr. Johnson"
            />
          </div>

          <div className="post-planner-milestone-input">
            <label htmlFor="milestone-type">
              Type
            </label>
            <select
              id="milestone-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="birthday">Birthday</option>
              <option value="work-anniversary">Work Anniversary</option>
            </select>
          </div>

          <div className="post-planner-milestone-input">
            <label htmlFor="milestone-date">
              Date
            </label>
            <input
              type="date"
              id="milestone-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="post-planner-milestone-add"
          style={{
            all: 'unset',
            boxSizing: 'border-box',
            width: '100%',
            marginTop: '8px',
            border: 'none',
            borderRadius: '6px',
            background: 'linear-gradient(130deg, #0d66c7, #f59e0b)',
            color: '#fff',
            padding: '8px 12px',
            minHeight: '0',
            maxHeight: '32px',
            height: '32px',
            lineHeight: '1.2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(13, 102, 199, 0.18)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            overflow: 'visible',
            position: 'static',
            fontFamily: 'inherit'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 102, 199, 0.22)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 102, 199, 0.18)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '12px', height: '12px' }}>
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Milestone
        </button>
      </div>

      {/* Milestone List */}
      {milestones.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
            Added Milestones ({milestones.length})
          </div>
          <div className="post-planner-milestone-list" style={{ maxHeight: '12rem', overflowY: 'auto' }}>
            {milestones.map((milestone) => (
              <div
                key={milestone.id}
                className="post-planner-milestone-item"
              >
                <div style={{ flexGrow: 1 }}>
                  <span style={{ fontWeight: 500, color: '#111827' }}>{milestone.name}</span>
                  <span style={{ color: '#6b7280', margin: '0 0.5rem' }}>•</span>
                  <span style={{ color: '#4b5563' }}>{getTypeLabel(milestone.type)}</span>
                  <span style={{ color: '#6b7280', margin: '0 0.5rem' }}>•</span>
                  <span style={{ color: '#4b5563' }}>{formatDate(milestone.month, milestone.day)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(milestone.id)}
                  aria-label="Remove milestone"
                  style={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    padding: '0',
                    margin: '0',
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    minHeight: '20px',
                    maxWidth: '20px',
                    maxHeight: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    flexGrow: 0,
                    borderRadius: '50%',
                    backgroundColor: '#fee2e2',
                    color: '#ef4444',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    lineHeight: 1,
                    border: 'none',
                    outline: 'none',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    verticalAlign: 'middle'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#fecaca';
                    e.currentTarget.style.color = '#dc2626';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.color = '#ef4444';
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
